import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

const BASE_URL = 'https://hiremymotorhome.co.uk';

/**
 * Sets the correct <link rel="canonical"> for every route.
 *
 * Design choices:
 * - Fires on NavigationEnd (after component ngOnInit), so it runs on both SSR
 *   and client-side navigation without any platform guards.
 * - Strips query params and fragments — canonicals must be clean URLs.
 * - Root path canonicalises to BASE_URL + "/" (trailing slash preserved).
 *   All other paths have trailing slashes stripped.
 * - On 404/unmatched routes the canonical reflects the URL the router settled on
 *   (typically the last matched parent), which is acceptable since those routes
 *   carry noindex via SeoService.
 * - Removes ALL existing <link rel="canonical"> elements before inserting the
 *   new one, preventing duplicates left over from index.html or prior navigations.
 * - Subscription is torn down via takeUntilDestroyed() when the platform is
 *   destroyed (each SSR request or TestBed teardown). The constructor-level
 *   setup is inherently idempotent — it runs exactly once per service instance.
 */
@Injectable({ providedIn: 'root' })
export class CanonicalService {
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((e) => this.setCanonical(e.urlAfterRedirects));
  }

  private setCanonical(url: string): void {
    const path = url.split('?')[0].split('#')[0];
    const normalizedPath = path === '/' ? '/' : path.replace(/\/$/, '');
    const href = `${BASE_URL}${normalizedPath}`;

    Array.from(this.document.querySelectorAll('link[rel="canonical"]')).forEach((el) =>
      el.remove(),
    );

    const link = this.document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', href);
    this.document.head.appendChild(link);
  }
}
